import React, { ReactElement, ReactNode } from "react";
import Head from "next/head";
import Header from "@components/shared/header";
import BusinessCopyMachinesLogo from "@components/businesscopymachines/logo";
import { useModal } from "@hooks/useModalContext";
import dynamic from "next/dynamic";

import colors from "@styles/variables/businessCopyMachinesVariables.module.scss";
import { useDomainContext } from "@hooks/useDomainContext";
import Mobile from "@components/shared/blogPost/sideWidget/mobile";

const Footer = dynamic(() => import("@components/shared/footer"));
const Modal = dynamic(() => import("@components/shared/modal"));

const CloseIcon = dynamic(() => import("@components/svg/closeIcon"));

const TermsAndConditions = dynamic(
    () => import("@components/shared/legal/termsAndConditions"),
);

const PrivacyPolicy = dynamic(
    () => import("@components/shared/legal/privacyPolicy"),
);

export default function BusinessCopyMachinesWrapper({
    children,
}: {
    children: ReactNode;
}): ReactElement {
    const { modal, setModal } = useModal();
    const { visitDetails } = useDomainContext();
    const handleClose = () => {
        setModal({ active: false, content: undefined });
    };

    const { active: modalActive, content: modalContent } = modal;

    const getModalContent = () => {
        switch (modalContent) {
            case "privacy":
                return (
                    <PrivacyPolicy
                        category="BusinessCopyMachines"
                        domain="BusinessCopyMachines.com"
                    />
                );
            case "terms":
                return (
                    <TermsAndConditions
                        category="BusinessCopyMachines"
                        domain="BusinessCopyMachines.com"
                    />
                );
            case "sideWidget":
                return <Mobile />;
            default:
                return null;
        }
    };

    return (
        <>
            <Head>
                <link rel="preconnect" href="https://fonts.googleapis.com" />
                <link rel="preconnect" href="https://fonts.gstatic.com" />
                <link
                    href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;500;700&display=swap"
                    rel="stylesheet"
                />
            </Head>

            <div style={{ fontFamily: `'Poppins', sans-serif` }}>
                <Header
                    textColor="#686868"
                    slogan={`The Best Digital Copier Companies In ${visitDetails.region}`}
                >
                    <BusinessCopyMachinesLogo />
                </Header>
                <main className="max-w-6xl mx-auto block px-4vw lg:px-4">
                    {children}
                </main>
                <Footer textColor={colors.footerColor} />
                <Modal
                    onClose={handleClose}
                    active={modalActive}
                    closeIcon={<CloseIcon />}
                    backdropClosable
                    modalStyles={{
                        display: "flex",
                        flexDirection: "column",
                        maxHeight:
                            modalContent === "sideWidget" ? "80vh" : "100%",
                    }}
                    modalClassName="h-[90vh] rounded"
                    bodyStyles={{ overflowY: "scroll" }}
                    width={900}
                    header={
                        modalContent === "privacy"
                            ? "Privacy Policy"
                            : modalContent === "terms"
                            ? "Terms and Conditions"
                            : modalContent === "sideWidget"
                            ? "Contents"
                            : undefined
                    }
                >
                    {getModalContent()}
                </Modal>
            </div>
        </>
    );
}
